import { useEffect, useRef, useState } from 'react';
import { Box, Divider, HStack, Image, Text, VStack } from '@chakra-ui/react';
// @ts-ignore
import DefaultLogo from '../../../assets/icons/DefaultLogo.svg';
import { useAppSelector } from '../../../redux/hooks';
import { getAppAnalytics } from '../../../redux/viewApp/appAnalytics/selectors';
import { getAppLogMetricsFromPastWeek } from '../../../utils/app-analytics/app-metrics';
import { fetchAppLogsFromPastWeek } from '../../../utils/app-analytics/fetch-logs';

export const AppMetrics = () => {
  const appAnalytics = useAppSelector(getAppAnalytics);
  const [appLogs, setAppLogs] = useState<any[]>([]); // Add type annotation
  const [avgTimeToClaim, setAvgTimeToClaim] = useState(0);
  const [avgClaimsPerWeek, setAvgClaimsPerWeek] = useState(0);

  const isAppLogsFetched = useRef(false);

  // round successRate to 2 decimal places
  const successRate =
    Math.round(
      ((appAnalytics.appDetail?.successSessions ?? 0) /
        (appAnalytics.appDetail?.totalSessions ?? 1)) *
        100 *
        100
    ) / 100 || 0;

  useEffect(() => {
    const fetchData = async () => {
      if (appLogs.length === 0 && !isAppLogsFetched.current) {
        // Remove optional chaining
        const appLogData = await fetchAppLogsFromPastWeek(appAnalytics.appDetail.appId);
        setAppLogs(appLogData);
        const { totalSessions, avgTimeForWeek } =
          getAppLogMetricsFromPastWeek(appLogData);

        setAvgClaimsPerWeek(totalSessions);
        // round avgTimeForWeek to 2 decimal places
        setAvgTimeToClaim(
          isNaN(avgTimeForWeek) ? 0 : Math.round(avgTimeForWeek * 100) / 100
        );
        isAppLogsFetched.current = true;
      }
    };
    // console.log('appAnalytics', appAnalytics);
    // console.log('appLogs', appLogs);
    fetchData();
  }, [appLogs, appAnalytics.appDetail.appId]); // Add dependencies

  return (
    <Box width="100%" borderRadius="16px" mt="30px" bgColor="#F7F9FB" boxShadow="2px">
      <VStack align="flex-start" padding={6} w="100%" h="100%" spacing="30px">
        <HStack w="100%" h="100%" alignItems="flex-start" justifyContent="space-between">
          <VStack spacing={3} w="80%" h="100%" alignItems="flex-start">
            <VStack spacing={2} alignItems="flex-start">
              <Text
                color="black"
                className="medium-font"
                style={{ fontWeight: 650 }}
                whiteSpace="pre-line"
                noOfLines={1}
                fontWeight="bold"
                fontFamily="inter"
              >
                {appAnalytics.appDetail.name}
              </Text>
              <Text
                color="gray"
                className="x-small-font"
                whiteSpace="pre-line"
                fontFamily="inter"
              >
                Application Id: {appAnalytics.appDetail.appId}
              </Text>
            </VStack>
            <HStack
              w="100%"
              mt="12px"
              h="fit-content"
              justifyContent="space-between"
              spacing={15}
            >
              <VStack alignItems="flex-start" spacing={2}>
                <Text className="x-small-font" color="black" fontFamily="inter">
                  Claims so far
                </Text>
                <Text
                  className="medium-font"
                  style={{ fontWeight: 600 }}
                  color="black"
                  fontWeight="bold"
                  fontFamily="inter"
                >
                  {appAnalytics.appDetail?.totalSessions || 0}
                </Text>
              </VStack>
              <VStack h={12} flexDir="column" alignItems="flex-start">
                <Divider
                  orientation="vertical"
                  borderColor="lightgray" // Custom color
                  borderWidth="1px" // Custom thickness
                />
              </VStack>
              <VStack alignItems="flex-start" spacing={2}>
                <Text className="x-small-font" color="black" fontFamily="inter">
                  Claims in the last week
                </Text>
                <Text
                  className="medium-font"
                  style={{ fontWeight: 600 }}
                  color="black"
                  fontWeight="bold"
                  fontFamily="inter"
                >
                  {avgClaimsPerWeek || 0}
                </Text>
              </VStack>
              <VStack h={12} flexDir="column" alignItems="flex-start">
                <Divider
                  orientation="vertical"
                  borderColor="lightgray" // Custom color
                  borderWidth="1px" // Custom thickness
                />
              </VStack>
              <VStack alignItems="flex-start" spacing={2}>
                <Text className="x-small-font" color="black" fontFamily="inter">
                  Success Rate
                </Text>
                <Text
                  className="medium-font"
                  style={{ fontWeight: 600 }}
                  color="black"
                  fontWeight="bold"
                  fontFamily="inter"
                >
                  {successRate}%
                </Text>
              </VStack>
              <VStack h={12} flexDir="column" alignItems="flex-start">
                <Divider
                  orientation="vertical"
                  borderColor="lightgray" // Custom color
                  borderWidth="1px" // Custom thickness
                />
              </VStack>
              <VStack alignItems="flex-start" spacing={2}>
                <Text className="x-small-font" color="black" fontFamily="inter">
                  Avg Time to Claim
                </Text>
                <Text
                  className="medium-font"
                  style={{ fontWeight: 600 }}
                  color="black"
                  fontWeight="bold"
                  fontFamily="inter"
                >
                  {avgTimeToClaim ?? '0'} seconds
                </Text>
              </VStack>
            </HStack>
          </VStack>
          <Image
            src={
              appAnalytics.appDetail?.appImageUrl
                ? appAnalytics.appDetail.appImageUrl
                : DefaultLogo
            }
            borderRadius="42px"
            w="52px"
            h="52px"
          />
        </HStack>
      </VStack>
    </Box>
  );
};
